import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import Skyline from "../components/Skyline";
import Seo from "../components/Seo";
import { submitDemoRequest } from "../lib/api";

const TEAM_SIZES = ["Just me", "2–5", "6–20", "21–50", "50+"];

export default function BookDemo() {
  const [form, setForm] = useState({ name: "", email: "", company: "", team_size: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please fill in your name, email and company.");
      return;
    }
    setSubmitting(true);
    try {
      await submitDemoRequest(form);
      setDone(true);
      toast.success("Request received, check your inbox for confirmation.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full bg-transparent border-b border-neutral-300 py-3 text-lg outline-none transition-colors duration-300 focus:border-black placeholder:text-neutral-400";

  return (
    <div data-testid="book-demo-page" className="pt-32 md:pt-44">
      <Seo
        title="Book a Discovery Call | Quexify"
        description="Book a 30-minute discovery call with Quexify. We'll learn your tools, match you with a dedicated transaction coordinator, and get you live within days."
        path="/book-demo"
      />
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-40">
        <div className="grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <Reveal>
              <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium leading-[0.9]">
                Close more,<br />chase less.
              </h1>
              <p className="mt-8 text-lg text-neutral-500 leading-relaxed max-w-md">
                A 30-minute call to learn your tools and where things slip today. We'll match you with a dedicated coordinator and have you live within days.
              </p>
              <div className="mt-16 w-40 opacity-10 hidden md:block">
                <Skyline className="w-full h-40" animate={false} />
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-3xl border border-black/10 bg-[#fafafa] p-12 md:p-16"
                data-testid="demo-success"
              >
                <div className="w-14 h-14 rounded-full bg-[#0a0a0a] text-white flex items-center justify-center">
                  <Check size={26} />
                </div>
                <h2 className="mt-8 font-display text-3xl md:text-4xl tracking-tight font-medium">You're all set, {form.name.split(" ")[0]}.</h2>
                <p className="mt-4 text-neutral-500 text-lg leading-relaxed">
                  We've received your request and sent a confirmation to <span className="text-black">{form.email}</span>. An operations specialist will reach out shortly.
                </p>
              </motion.div>
            ) : (
              <Reveal>
                <form onSubmit={handleSubmit} className="space-y-10" data-testid="demo-form">
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-sm text-neutral-500">Full name</label>
                      <input className={field} value={form.name} onChange={update("name")} placeholder="Jane Doe" data-testid="input-name" />
                    </div>
                    <div>
                      <label className="text-sm text-neutral-500">Work email</label>
                      <input type="email" className={field} value={form.email} onChange={update("email")} placeholder="jane@brokerage.com" data-testid="input-email" />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-neutral-500">Company</label>
                    <input className={field} value={form.company} onChange={update("company")} placeholder="Brokerage or team name" data-testid="input-company" />
                  </div>

                  <div>
                    <label className="text-sm text-neutral-500 block mb-4">Team size</label>
                    <div className="flex flex-wrap gap-3">
                      {TEAM_SIZES.map((t) => (
                        <button
                          type="button"
                          key={t}
                          onClick={() => setForm((f) => ({ ...f, team_size: t }))}
                          className={`rounded-full border px-5 py-2 text-sm transition-colors duration-300 ${
                            form.team_size === t ? "bg-[#0a0a0a] text-white border-black" : "border-neutral-300 hover:border-black"
                          }`}
                          data-testid={`team-size-${t}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-neutral-500">What can we take off your plate?</label>
                    <textarea rows={3} className={`${field} resize-none`} value={form.message} onChange={update("message")} placeholder="Optional, tell us where deals get stuck." data-testid="input-message" />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center gap-2 rounded-full bg-[#0a0a0a] text-white px-9 py-4 text-lg transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50"
                    data-testid="demo-submit"
                  >
                    {submitting ? "Sending…" : "Request my discovery call"}
                    <ArrowUpRight size={20} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
